import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FetchDealerOrdersResponse,
  DealerOrdersFilterFieldsType,
  IDealerOrder,
  DealerOrderProductsInfosType,
  DealerOrdersBodyType,
  DealerOrdersFilterType,
  DealerOrdersInfoType,
  DealerOrdersSliceState as DealerOrdersSliceState,
  ChangeDealerOrderStatusBodyType,
} from "./types";
import $api from "@/shared/http";
import { ApiRejectPayload, createApiRejectPayload } from "@/shared/http/types";
import { revertAll } from "@/app/model/actions";
import { PageSizeType } from "@/app/model/types";
import { formatCreatedFilterField } from "./lib/formatCreatedFilter";

const initialState: DealerOrdersSliceState = {
  ordersList: [],
  selectedOrderData: null,
  needUpdate: false,
  allOrdersId: [],
  filters: [],
  allCreatedDates: [],
  onlyMyDealers: false,
  selectedOrdersId: [],
  totalAmount: 0,
  totalCount: 0,
  selectedOrderFilters: {},
  orderProductsInfos: [],
  ordersInfo: {
    count: 0,
    priceSumm: 0,
  },
  error: null,
  isLoading: false,
  isFilterLoading: false,
  selectedDates: undefined,
  sortRules: {
    type: "desc",
    field: "created",
  },
  searchCode: null,
  pageSize: (+localStorage.getItem("psizeDealerOrder") as PageSizeType) || 20,
  totalItemsCount: 0,
  selectedOrderIdNum: null,
  canCreate: false,
};

const dealerOrderThunkRejectConfig = { rejectValue: {} as ApiRejectPayload };

/* Загрузка заказов */
export const fetchDealerOrders = createAsyncThunk<
  FetchDealerOrdersResponse,
  "" | DealerOrdersBodyType,
  typeof dealerOrderThunkRejectConfig
>("dealerOrders/fetchOrders", async (value = "", { rejectWithValue }) => {
  let body = {
    order: {
      type: "desc",
      field: "created",
    },
    pagination: {
      pageSize: +localStorage.getItem("psizeN") || 20,
      pageNumber: 1,
    },
    filters: [] as object[],
  };

  if (value) {
    const { filters, order, pagination } = value;

    body = {
      order,
      pagination,
      filters,
    };
  }

  try {
    const response = await $api.post<FetchDealerOrdersResponse>(
      "/dealerorders",
      JSON.stringify(body),
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка фильтров заказов */
export const fetchDealerOrdersFilters = createAsyncThunk<
  DealerOrdersFilterType,
  DealerOrdersFilterFieldsType | [],
  typeof dealerOrderThunkRejectConfig
>(
  "dealerorders/fetchFilters",
  async (fields = [], { getState, rejectWithValue }) => {
    const { allCreatedDates } = (
      getState() as { orders: DealerOrdersSliceState }
    ).orders;
    const fieldsForRequest = formatCreatedFilterField(fields, allCreatedDates);

    const body = {
      filters: Object.keys(fieldsForRequest).length
        ? [{ type: "eq", fields: fieldsForRequest }]
        : [],
    };

    try {
      const response = await $api.post("/filters/dealerorder", body);
      return response.data?.filters;
    } catch (error) {
      return rejectWithValue(createApiRejectPayload(error));
    }
  },
);

/* Загрузка заказа по ид */
export const getOneDealerOrderById = createAsyncThunk<
  IDealerOrder,
  string,
  typeof dealerOrderThunkRejectConfig
>("dealerorders/getOneOrderById", async (id, { rejectWithValue }) => {
  try {
    const response = await $api.get<IDealerOrder>(`/dealerorders/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка продуктов заказа */
export const fetchDealerOrderProductsInfo = createAsyncThunk<
  DealerOrderProductsInfosType[],
  void,
  typeof dealerOrderThunkRejectConfig
>("dealerOrder/fetchOrderProductsInfo", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<DealerOrderProductsInfosType[]>(
      "/dealerorders/products/info",
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка информации заказа */
export const fetchDealerOrdersInfo = createAsyncThunk<
  DealerOrdersInfoType,
  "" | Pick<DealerOrdersBodyType, "filters">,
  typeof dealerOrderThunkRejectConfig
>("dealerOrder/fetchDealerOrdersInfo", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<DealerOrdersInfoType>("/dealerorders/info");
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

export const changeDealerOrderStatus = createAsyncThunk<
  boolean,
  ChangeDealerOrderStatusBodyType,
  typeof dealerOrderThunkRejectConfig
>("dealerOrder/changeDealerOrderStatus", async (body, { rejectWithValue }) => {
  try {
    const newBody = [body];
    const response = await $api.post<boolean>(
      "/dealerorders/status",
      JSON.stringify(newBody),
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

export const cancelDealerOrder = createAsyncThunk<
  boolean,
  string,
  typeof dealerOrderThunkRejectConfig
>("dealerOrder/cancelDealerOrder", async (id, { rejectWithValue }) => {
  try {
    const response = await $api.post<boolean>(`/dealerorders/${id}/cancel`);
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

export const dealerOrdersSlice = createSlice({
  name: "dealerOrders",
  initialState,
  reducers: {
    setOrdersListMock(state, action) {
      state.ordersList = action.payload;
    },
    setOnlyMyDealers(state, action) {
      state.onlyMyDealers = action.payload;
    },
    setSelectedOrderData(state, action) {
      state.selectedOrderData = action.payload;
    },
    setSelectedOrderFilters(state, action) {
      state.searchCode = null;
      state.selectedOrderFilters = action.payload;
    },
    setSelectedDates(state, action) {
      state.searchCode = null;
      state.selectedDates = action.payload;
      if (action.payload.length) {
        state.selectedOrderFilters.created = action.payload;
      } else {
        const { created, ...rest } = state.selectedOrderFilters;
        state.selectedOrderFilters = rest;
      }
    },
    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.selectedDates = [];
      state.selectedOrderFilters = {};
      state.searchCode = action.payload;
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    setSelectedOrderNum(
      state,
      action: PayloadAction<typeof state.selectedOrderIdNum>,
    ) {
      state.selectedOrderIdNum = action.payload;
    },
    setNeedUpdate(state, action: PayloadAction<typeof state.needUpdate>) {
      state.needUpdate = action.payload;
    },
    clearOrdersError(state) {
      state.error = null;
      state.isLoading = false;
    },
    resetFilters(state) {
      state.searchCode = null;
      state.selectedDates = undefined;
      state.selectedOrderFilters = {};
      state.onlyMyDealers = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(fetchDealerOrders.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(
      fetchDealerOrders.fulfilled,
      (state, action: PayloadAction<FetchDealerOrdersResponse>) => {
        const res = action.payload;
        state.ordersList = res.items;
        const ids = res.items?.reduce((acc, order) => {
          return (acc = [...acc, order.id]);
        }, []);
        state.allOrdersId = ids;
        state.needUpdate = false;
        state.isLoading = false;
        state.totalItemsCount = res.totalCount;
      },
    );

    builder.addCase(fetchDealerOrders.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    /* Загрузка фильтров заказов */
    builder.addCase(fetchDealerOrdersFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });

    builder.addCase(fetchDealerOrdersFilters.fulfilled, (state, action) => {
      state.filters = action.payload;
      state.isFilterLoading = false;

      /* Полный справочник дат берём только из нефильтрованного запроса */
      const requestedFields = action.meta.arg;
      if (!requestedFields || !Object.keys(requestedFields).length) {
        state.allCreatedDates = action.payload?.[0]?.fields?.created || [];
      }
    });

    builder.addCase(fetchDealerOrdersFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload;
    });

    /* Загрузка продуктов заказа */
    builder.addCase(fetchDealerOrderProductsInfo.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(
      fetchDealerOrderProductsInfo.fulfilled,
      (state, action: PayloadAction<DealerOrderProductsInfosType[]>) => {
        state.orderProductsInfos = action.payload;
        state.isLoading = false;
      },
    );

    builder.addCase(fetchDealerOrderProductsInfo.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    /* Загрузка информации заказа */
    builder.addCase(fetchDealerOrdersInfo.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      fetchDealerOrdersInfo.fulfilled,
      (state, action: PayloadAction<DealerOrdersInfoType>) => {
        state.ordersInfo = action.payload;
        state.isLoading = false;
      },
    );
    builder.addCase(fetchDealerOrdersInfo.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    /* Загрузка заказа по ид */
    builder.addCase(getOneDealerOrderById.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      getOneDealerOrderById.fulfilled,
      (state, action: PayloadAction<IDealerOrder>) => {
        state.selectedOrderData = action.payload;
        state.isLoading = false;
      },
    );
    builder.addCase(getOneDealerOrderById.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    /* Смена статуса заказа (отгрузка подтверждённых позиций) */
    builder.addCase(changeDealerOrderStatus.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(changeDealerOrderStatus.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(changeDealerOrderStatus.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    /* Отгрузка (отмена) заказа */
    builder.addCase(cancelDealerOrder.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(cancelDealerOrder.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(cancelDealerOrder.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
  },
});

export const {
  setOrdersListMock,
  setSelectedOrderFilters,
  setSelectedDates,
  setSelectedOrderData,
  setSortRules,
  setSearchCode,
  setPageSize,
  setOnlyMyDealers,
  setSelectedOrderNum,
  clearOrdersError,
  resetFilters,
  setNeedUpdate,
} = dealerOrdersSlice.actions;
